/**
 * Global Search API client + TanStack Query hook — spec-46 W3
 *
 * Wraps GET /api/v1/search (jylaw-service :8180 via vite proxy).
 * Pattern: follows src/api/conflictCheck.ts (typed fetch + useQuery).
 *
 * Hooks exported:
 *   useGlobalSearch(query, kinds?, statuses?) — debounced 300ms, skip when empty
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SearchKind =
  | 'company'
  | 'matter'
  | 'retainer_contract'
  | 'doc'
  | 'email'
  | 'proposal'
  | 'task';

export const ALL_SEARCH_KINDS: SearchKind[] = [
  'company',
  'matter',
  'retainer_contract',
  'doc',
  'email',
  'proposal',
  'task',
];

export interface StatusPill {
  label: string;
  color: string;
}

export interface SearchResult {
  kind: SearchKind;
  id: string;
  title: string;
  subtitle: string | null;
  snippet: string | null;
  link: string;
  rank: number;
  status: string | null;
  status_pill: StatusPill | null;
}

export interface SearchResponse {
  results: SearchResult[];
  total_per_kind: Record<SearchKind, number>;
}

// ---------------------------------------------------------------------------
// Helpers — match existing portal pattern (conflictCheck.ts / dashboard.ts)
// ---------------------------------------------------------------------------

function tenantFromJwt(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return claims.tenant_id || claims.tenant || null;
  } catch { return null; }
}

function getAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('access_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenant = localStorage.getItem('tenant_id') || (token ? tenantFromJwt(token) : null);
    if (tenant) headers['X-Tenant-ID'] = tenant;
  } catch { /* SSR safety */ }
  return headers;
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

async function fetchSearch(
  query: string,
  kinds?: SearchKind[],
  statuses?: string[],
): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query });
  if (kinds && kinds.length > 0) params.set('kinds', kinds.join(','));
  if (statuses && statuses.length > 0) params.set('status', statuses.join(','));
  const res = await fetch(`/api/v1/search?${params.toString()}`, { headers: getAuthHeaders() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || `HTTP ${res.status}`);
  }
  return res.json() as Promise<SearchResponse>;
}

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(value), delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const searchKeys = {
  all: ['global-search'] as const,
  search: (query: string, kinds?: SearchKind[], statuses?: string[]) =>
    [...searchKeys.all, query, kinds ?? [], statuses ?? []] as const,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useGlobalSearch — debounced global search hook.
 *
 * - 300ms debounce on query
 * - Skip fetch when debounced query is empty/whitespace
 * - staleTime: 30s; keepPreviousData: true
 */
export function useGlobalSearch(
  query: string,
  kinds?: SearchKind[],
  statuses?: string[],
) {
  const debouncedQuery = useDebounce(query, 300);
  const enabled = debouncedQuery.trim().length > 0;

  return useQuery({
    queryKey: searchKeys.search(debouncedQuery, kinds, statuses),
    queryFn: () => fetchSearch(debouncedQuery, kinds, statuses),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev, // keepPreviousData equivalent in TanStack v5
    retry: 1,
  });
}
